import {
    seeProjectPrice,
    userCanSeeBestPrice,
    userCanSeeBestPriceOnProductCard
} from '@ab-core-functions/user-permission';
import type { LineItemProduct } from '@ab-core/graphql/dist';
import { useBestPrices, useProductsAvailabilitiesWithChannel } from '@ab-core/hooks-v2';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import type { DefaultScalableProductCardProps } from '../cardLayout';
import { fallBackPosition, fallBackQuantityUnit, fallBackSalesUnit } from './fallBackStates';
import { isSpecialSupplierProduct, SPECIAL_SUPPLIER_IDS } from './utils';

const mapProductTags = (product: LineItemProduct, aggregatedQuantity?: number) => ({
    product: {
        isAbakus: product.bonus?.isAbakus,
        isAbakusPlus: product.bonus?.isAbakusPlus,
        topProduct: product.bonus?.isTopProduct,
        isTecselect: product.bonus?.isTecselect,
        isPromotion: product.bonus?.isPromotion,
        scoreCardIndex: product.scoreCardIndex,
        is125Premium: product?.bonus?.isPremiumProduct,
        is125Vip: product?.bonus?.isVipProduct,
        isAdditionalSalesPromotion: product?.bonus?.isAdditionalSalesPromotion,
        isItShop: isSpecialSupplierProduct(SPECIAL_SUPPLIER_IDS.AVM, product.supplierId, aggregatedQuantity),
        ean: product.ean || undefined,
        successorProduct: product.successorProduct || undefined
    }
});

export const useMappedLineItemToScalableProductV2 = (
    products?: Array<LineItemProduct>
): Array<DefaultScalableProductCardProps['product']> => {
    const { activeCart } = getActiveCart();
    const hasBestPrice = userCanSeeBestPrice();
    const hasBestPriceOnProductCard = userCanSeeBestPriceOnProductCard();
    const skus: Array<string> = [];
    let projectNumber =
        activeCart?.custom.customFieldsRaw.find((element) => element.name === 'cart-projectNr')?.value || '0';
    const canSeeProjectPrice = seeProjectPrice();
    projectNumber = canSeeProjectPrice ? projectNumber : '0';
    products?.map((product) => {
        if (product?.sku) {
            skus.push(product.sku);
        }
    });

    const { bestPrices } = useBestPrices(skus, (!hasBestPrice || !hasBestPriceOnProductCard) && skus.length === 0);
    const { productsAvailabilitiesWithChannel } = useProductsAvailabilitiesWithChannel(
        {
            skus,
            cartId: activeCart?.id
        },
        !activeCart
    );

    const productList: Array<DefaultScalableProductCardProps['product']> = [];
    products?.map((product) => {
        const productAvailability = productsAvailabilitiesWithChannel?.find(
            (available) => available.sku === product.sku
        );
        const aggregatedQuantity = productAvailability?.aggregatedMainChannelQuantity;
        const maxQuantity = product.isLimitedQuantity ? aggregatedQuantity : undefined;
        const position = Number(product.position) || fallBackPosition;

        productList.push({
            lineItemId: product.id,
            defaultQuantity: product.quantity || product.salesUnit || fallBackSalesUnit,
            defaultPositionText: product.positionText || '',
            sku: product.sku || '',
            information: { reach: product.reachInfo },
            tags: mapProductTags(product, aggregatedQuantity),
            ids: {
                articleNumber: product.sku,
                ean: product.ean,
                customerArticleNumber: product.customerArticleNumber
            },
            image: {
                imgSrc: product.thumbnail || '',
                sku: product.sku,
                position
            },
            name: {
                text: product.productName,
                sku: product.sku,
                position
            },
            additionalLinks: {
                sku: product.sku,
                hasAccessories: product.hasAccessories,
                hasAlternatives: product.hasAlternatives
            },
            prices: {
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit
            },
            availability: {
                availability: productAvailability,
                quantityUnit: product.quantityUnit || fallBackQuantityUnit
            },
            order: {
                bestPrice: bestPrices?.find((bestPrice) => bestPrice.sku === product.sku),
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit,
                sku: product.sku || '',
                isUnqualifiedContractProduct: product.isUnqualifiedContractProduct || false,
                chargeNr: product.chargeNr,
                packagingSize: product?.packagingSize || '',
                cartId: activeCart?.id,
                supplierId: product.supplierId,
                stockQuantity: aggregatedQuantity ?? 0,
                ean: product.ean || '',
                nocut: product?.nocut,
                position,
                maxQuantity,
                projectNumber
            },
            comparison: {
                sku: product.sku,
                title: product.productName,
                imageSrc: product.thumbnail || ''
            }
        });
    });

    return productList;
};
